import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import _ from 'lodash';

import logo from 'client/assets/login/logo.svg';

import { analytics } from 'client/utils/analytics';
import { setShouldInitPubnub } from 'client/redux/ui';
import { selectShouldInitPubnub } from 'client/redux/ui/selectors';
import { useAppDispatch, useTranslation, useResponsive, useGAEventCommonProps, useAppSelector } from 'client/hooks';
import { setFormByUserType } from 'client/redux/login';
import { UserLoginType } from 'client/types';

import TransitionComponent from '../TransitionComponent';
import LoginForm from './Form';
import Icon from '../Icon';

import css from './LogIn.module.scss';

interface Props {
  className?: string;
  userLoginType: UserLoginType;
}

const LogIn: React.FC<Props> = (props) => {
  const { className = '', userLoginType } = props;
  const [isIncorrectCode, setIsIncorrectCode] = useState<boolean>(false);
  const { translate } = useTranslation('login');
  const dispatch = useAppDispatch();
  const [isMobile] = useResponsive('MOBILE');
  const [isShowPopup, setIsShowPopup] = useState<boolean>(false);
  const { common } = useGAEventCommonProps();
  const shouldInitPubnub = useAppSelector(selectShouldInitPubnub);

  useEffect(() => {
    if (
      userLoginType === UserLoginType.custom ||
      userLoginType === UserLoginType.regular ||
      userLoginType === UserLoginType.sms
    ) {
      setIsShowPopup(true);
    }
  }, [userLoginType]);

  const closeLoginPopup = (): void => {
    setIsIncorrectCode(false);
    setIsShowPopup(false);

    _.delay(() => {
      dispatch(setFormByUserType(UserLoginType.none));
    }, TransitionComponent.OPACITY_TRANSITION_TIME);
  };

  const onCloseBtnClick = (): void => {
    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: 'Close',
    });
    closeLoginPopup();
  };

  const onLoginFormSubmitSuccess = (): void => {
    if (!shouldInitPubnub) dispatch(setShouldInitPubnub(true));
    closeLoginPopup();
  };

  const renderInnerStructure = () => {
    return (
      <>
        <button className={css.btnClose} onClick={onCloseBtnClick}>
          <Icon type="close" color={isMobile ? '#fff' : '#000'} />
        </button>
        <div className={css.header}>
          <div className={css.logo}>
            <img src={logo} alt="logo" />
            <span>{translate('header.logo')}</span>
          </div>
          <div className={css.title}>
            <span>{translate(`header.${userLoginType}.title`)}</span>
          </div>
          <div className={css.subtitle}>
            <span>
              {isIncorrectCode && userLoginType === 'sms'
                ? translate(`header.${userLoginType}.subtitleError`)
                : translate(`header.${userLoginType}.subtitle`)}
            </span>
          </div>
        </div>
        <LoginForm
          userLoginType={userLoginType}
          onSuccess={onLoginFormSubmitSuccess}
          isIncorrectCode={isIncorrectCode}
          setIsIncorrectCode={setIsIncorrectCode}
          onJustListenBtnClick={closeLoginPopup}
        />
      </>
    );
  };

  const renderDesktop = () => {
    return <div className={css.contentWrapper}>{renderInnerStructure()}</div>;
  };

  const renderMobile = () => {
    return renderInnerStructure();
  };

  return (
    <TransitionComponent type="opacity" visible={isShowPopup}>
      <div className={classNames(css.logIn, !shouldInitPubnub && css.fullHeight, className)}>
        {isMobile ? renderMobile() : renderDesktop()}
      </div>
    </TransitionComponent>
  );
};

export default LogIn;
